'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveWallet, useAuth, useGetMarket, useGetThesesInfinite, useGetTrades } from '@morfi/backend-sdk-v2/client';
import { AnimatePresence, domMax, LazyMotion, m, type PanInfo, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { type PromptTrade, resolvePromptTrade, type TradeRow } from '@/components/market/resolve-prompt-trade';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { CancelCircleIcon, IdeaIcon } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { outcomeHueToStepVar, useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { useHydrated } from '@/hooks/ui/use-hydrated';
import { useFormattedCurrency } from '@/lib/currency';
import { VIEWER_TRADE_PROJECTION } from '@/lib/sdk/projections';
import { viewerMarketTradeQueryArgs } from '@/lib/sdk/viewer-market-trade-query';
import { MOTION_ENTRANCE } from '@/lib/ui/easing';
import { useLocalThesesStore } from '@/stores/local-theses';
import { type ThesisTradeContext, useThesisDialogStore } from '@/stores/thesis-dialog';

// ---------------------------------------------------------------------------
// Floating "Add thesis" prompt bar for the market-detail Debate tab (MOR-649).
//
// Mounted inside <MarketDebateSection> so it only lives while the Debate tab is
// open. Reuses the leaderboard "Your rank" bar pattern: portaled to body,
// `position: fixed` at the bottom, AnimatePresence enter/exit. Surfaces the
// viewer's most-recent un-thesised buy/sell on this market and opens the
// existing thesis dialog — the same `openDialog(...)` the post-trade toast uses.
//
// Visible only when: the viewer is authenticated and their newest buy/sell on
// this market has no thesis (backend-joined OR session-local overlay). It hides
// the instant a thesis is attached.
// ---------------------------------------------------------------------------

// Stacks ABOVE the mobile Trade CTA (`mobile-trade-shell.tsx`, fixed a few px
// above the browser chrome, `lg:hidden`). On desktop that CTA is gone
// (right-column sticky form), so the bar centres at the bottom like the
// leaderboard bar.
const POSITIONING_CLASSES = 'fixed inset-x-3 bottom-[72px] z-overlay lg:bottom-4 lg:inset-x-auto lg:left-1/2 lg:max-w-[640px] lg:w-[min(640px,calc(100vw-2rem))] lg:-translate-x-1/2'; // ARBITRARY: 72px clears the fixed mobile Trade CTA above the browser chrome; 640px desktop readability cap not expressible via spacing tokens.

const ENTER_OFFSET = {
  y: 12,
  opacity: 0
} as const;
const VISIBLE = {
  y: 0,
  opacity: 1
} as const;
interface MarketThesisPromptBarProps {
  marketPubkey: string;
  tradesFrom: string;
}

// ── Gate shell ───────────────────────────────────────────────────────────────

/**
 * Cheap outer guard so the data hooks below never fire for signed-out viewers.
 * Returning `null` here keeps the inner component (and its trades subscription)
 * unmounted until the viewer is eligible.
 */
export function MarketThesisPromptBar(t0) {
  const $ = _c(4);
  const {
    marketPubkey,
    tradesFrom
  } = t0;
  const {
    isAuthenticated,
    isLoading
  } = useAuth();
  const {
    activeWallet
  } = useActiveWallet();
  const userPubkey = activeWallet?.address;
  if (isLoading || !isAuthenticated || userPubkey === undefined || userPubkey.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== marketPubkey || $[1] !== tradesFrom || $[2] !== userPubkey) {
    t1 = <MarketThesisPromptBarInner marketPubkey={marketPubkey} tradesFrom={tradesFrom} userPubkey={userPubkey} />;
    $[0] = marketPubkey;
    $[1] = tradesFrom;
    $[2] = userPubkey;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

// ── Inner (data + render) ────────────────────────────────────────────────────

function MarketThesisPromptBarInner(t0) {
  const $ = _c(28);
  const {
    marketPubkey,
    tradesFrom,
    userPubkey
  } = t0;
  const t = useTranslations("thesis");
  const {
    formatD6
  } = useFormattedCurrency();
  const prefersReducedMotion = useReducedMotion();
  const openDialog = useThesisDialogStore(_temp);
  const localTheses = useLocalThesesStore(_temp2);
  const dismissedPrompts = useLocalThesesStore(_temp3);
  const dismissThesisPrompt = useLocalThesesStore(_temp4);
  const hydrated = useHydrated();
  let t1;
  if ($[0] !== marketPubkey || $[1] !== tradesFrom || $[2] !== userPubkey) {
    t1 = viewerMarketTradeQueryArgs({
      market: marketPubkey,
      user: userPubkey,
      from: tradesFrom
    });
    $[0] = marketPubkey;
    $[1] = tradesFrom;
    $[2] = userPubkey;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [...VIEWER_TRADE_PROJECTION];
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1) {
    t3 = {
      ...t1,
      fields: t2
    };
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const tradesQuery = useGetTrades(t3);
  const fallbackRows = tradesQuery.rows;
  const rows = useLiveListData(tradesQuery.queryKey, fallbackRows);
  let t4;
  if ($[7] !== userPubkey) {
    t4 = [userPubkey];
    $[7] = userPubkey;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== marketPubkey || $[10] !== t4 || $[11] !== userPubkey) {
    t5 = {
      authorId: userPubkey,
      marketPubkey,
      walletPubkeys: t4,
      sort: "recent"
    };
    $[9] = marketPubkey;
    $[10] = t4;
    $[11] = userPubkey;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const ownThesesQuery = useGetThesesInfinite(t5);
  const thesisSignatures = new Set((ownThesesQuery.rows as ReadonlyArray<{
    signature: string;
  }>).map(_temp5));
  let t6;
  if ($[13] !== marketPubkey) {
    t6 = {
      pubkey: marketPubkey
    };
    $[13] = marketPubkey;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const marketQuery = useGetMarket(t6);
  const outcomes = marketQuery.data?.outcomes ?? [];
  const resolvedTrade = resolvePromptTrade(rows, localTheses, thesisSignatures, outcomes, userPubkey);
  const promptTrade = resolvedTrade !== null && dismissedPrompts[resolvedTrade.signature] === undefined ? resolvedTrade : null;
  const reduceMotion = prefersReducedMotion === true;
  const initial = reduceMotion ? false : ENTER_OFFSET;
  if (!hydrated) {
    return null;
  }
  const t7 = createPortal;
  const T0 = LazyMotion;
  const t8 = domMax;
  const t9 = POSITIONING_CLASSES;
  const T1 = AnimatePresence;
  const t10 = "wait";
  const t11 = promptTrade !== null && <m.div key={promptTrade.signature} initial={initial} animate={VISIBLE} exit={reduceMotion ? VISIBLE : ENTER_OFFSET} transition={reduceMotion ? {
    duration: 0
  } : MOTION_ENTRANCE} drag="y" dragConstraints={{
    top: 0,
    bottom: 0
  }} dragElastic={{
    top: 0,
    bottom: 0.7
  }} onDragEnd={(_e, info) => {
    if (info.offset.y > 32 || info.velocity.y > 400) {
      dismissThesisPrompt(promptTrade.signature);
    }
  }}><PromptRow trade={promptTrade} summary={t.rich(promptTrade.direction === "buy" ? "tradeSummary.buy" : "tradeSummary.sell", {
      outcomeLabel: promptTrade.outcomeLabel ?? "",
      amount: formatD6(promptTrade.amountD6),
      outcome: chunks => <OutcomeName label={promptTrade.outcomeLabel ?? ""} index={promptTrade.outcomeIndex}>{chunks}</OutcomeName>
    })} actionLabel={t("prompt_bar.action")} dismissLabel={t("prompt_bar.dismiss")} onAdd={() => {
      const ctx = {
        outcomeIndex: promptTrade.outcomeIndex,
        direction: promptTrade.direction,
        amountD6: promptTrade.amountD6,
        outcomeLabel: promptTrade.outcomeLabel
      };
      openDialog(marketPubkey, promptTrade.signature, promptTrade.walletAddress, ctx);
    }} onDismiss={() => {
      dismissThesisPrompt(promptTrade.signature);
    }} /></m.div>;
  let t12;
  if ($[15] !== T1 || $[16] !== t11) {
    t12 = <T1 mode={t10}>{t11}</T1>;
    $[15] = T1;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12 || $[19] !== t9) {
    t13 = <div className={t9}>{t12}</div>;
    $[18] = t12;
    $[19] = t9;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== T0 || $[22] !== t13 || $[23] !== t8) {
    t14 = <T0 features={t8}>{t13}</T0>;
    $[21] = T0;
    $[22] = t13;
    $[23] = t8;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t14 || $[26] !== t7) {
    t15 = t7(t14, document.body);
    $[25] = t14;
    $[26] = t7;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  return t15;
}

// ── Presentation ─────────────────────────────────────────────────────────────

/**
 * Outcome name tinted with the chart LINE color for that outcome —
 * `outcomeHueToStepVar(hue, 400)`, the same `var(--outcome-{hue}-400)` the
 * chart series + legend dots use (via `useChartColors`). Applied inline because
 * the 400-step primitive has no generated Tailwind text utility (it lives in
 * `:root`, outside `@theme`); this mirrors `position-card` / `holdings-card`.
 */
function _temp5(r) {
  return r.signature;
}
function _temp4(s_2) {
  return s_2.dismissThesisPrompt;
}
function _temp3(s_1) {
  return s_1.dismissedPromptSignatures;
}
function _temp2(s_0) {
  return s_0.byTradeSignature;
}
function _temp(s) {
  return s.openDialog;
}
function OutcomeName(t0) {
  const $ = _c(10);
  const {
    label,
    index,
    children
  } = t0;
  let t1;
  if ($[0] !== index || $[1] !== label) {
    t1 = {
      title: label,
      index
    };
    $[0] = index;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    hue
  } = useOutcomeColor(t1);
  let t2;
  if ($[3] !== hue) {
    t2 = outcomeHueToStepVar(hue, 400);
    $[3] = hue;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = {
      color: t2
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== t3) {
    t4 = <span className="font-medium" style={t3}>{children}</span>;
    $[7] = children;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
interface PromptRowProps {
  trade: PromptTrade;
  summary: ReactNode;
  actionLabel: string;
  dismissLabel: string;
  onAdd: () => void;
  onDismiss: () => void;
}
function PromptRow(t0) {
  const $ = _c(16);
  const {
    summary,
    actionLabel,
    dismissLabel,
    onAdd,
    onDismiss
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Icon icon={IdeaIcon} aria-hidden={true} className="size-4 shrink-0 text-foreground-secondary" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== summary) {
    t2 = <div className="flex min-w-0 items-center gap-2">{t1}<span className="truncate text-body text-foreground-secondary">{summary}</span></div>;
    $[1] = summary;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== actionLabel || $[4] !== onAdd) {
    t3 = <AnimatedButton variant="primary" size="sm" onClick={onAdd} className="shrink-0">{actionLabel}</AnimatedButton>;
    $[3] = actionLabel;
    $[4] = onAdd;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <Panel tone="accent" density="compact" className="gap-2! py-2! overflow-hidden bg-background-secondary/70 shadow-elevation-sticky supports-backdrop-filter:backdrop-blur-xl border border-border-primary"><PanelContent className="flex items-center justify-between gap-3 py-1">{t2}{t3}</PanelContent></Panel>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Icon icon={CancelCircleIcon} aria-hidden={true} className="size-3.5" />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== dismissLabel || $[11] !== onDismiss) {
    t6 = <button type="button" aria-label={dismissLabel} onClick={onDismiss} className="absolute -right-1 -top-1 z-10 rounded-full border border-border-primary bg-background-secondary p-0.5 text-foreground-secondary transition-colors duration-fast hover:text-foreground-primary">{t5}</button>;
    $[10] = dismissLabel;
    $[11] = onDismiss;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t6) {
    t7 = <div className="relative">{t4}{t6}</div>;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}